/**
 * Withdrawal Settings Service
 *
 * Service for managing withdrawal settings and configurations
 * Integrates with configuration-service API
 */

import authService from './authService'
import { getOptionalTenantId, resolveTenantId } from '../utils/tenantUtils'
import { getConfigurationServiceBaseUrl } from '../lib/apiBaseUrl'

export interface WithdrawalSettings {
    depositFees: {
        bankAccountFee: number
        trueMoneyFee: number
    }
    withdrawalFees: {
        withdrawalFee: number
    }
    depositChannels: {
        bank: boolean
        trueMoney: boolean
        promptPay: boolean
        decimalReservation: boolean
    }
    withdrawalConditions: {
        minWithdrawalAmount: number
        gamesBeforeWithdrawal: number
        dailyWithdrawalLimit: number
        dailyWithdrawalAmountLimit: number
        autoWithdrawalFromFirstDeposit: string
        withdrawRequestCooldownSeconds: number
    }
    autoWithdrawalConditions: {
        autoApproveEnabled: boolean
        maxAutoApprovalAmount: number
        autoApprovalInterval: number
        autoPayoutEnabled: boolean
        maxWithdrawalPerTransaction: number
        forceDisableAmountChange: boolean
    }
    withdrawalBot: {
        shutdownTime: string
        activationTime: string
    }
}

export interface WithdrawalSettingsResponse {
    success: boolean
    data: {
        settings: WithdrawalSettings
        isDefault: boolean
        tenantScope: string
        environment: string
        lastUpdated?: string
        version?: number
    }
    service: string
}

export interface WithdrawalConfigResponse {
    success: boolean
    data: {
        key: string
        value: any
        tenantScope: string
        environment: string
    }
    service: string
}

class WithdrawalSettingsService {
    private baseUrl: string

    constructor() {
        this.baseUrl = `${getConfigurationServiceBaseUrl()}/withdrawal`
    }

    private async resolveRequestTenantId(): Promise<string> {
        const currentUser = authService.getCurrentUser()
        if (currentUser?.tenantId) {
            return currentUser.tenantId
        }

        const runtimeTenantId = getOptionalTenantId(currentUser || undefined)
        if (runtimeTenantId) {
            return runtimeTenantId
        }

        return resolveTenantId(currentUser || undefined)
    }

    private buildHeaders(tenantId: string): Record<string, string> {
        const token =
            localStorage.getItem('accessToken') ||
            localStorage.getItem('authToken') ||
            localStorage.getItem('token')

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantId,
            'X-Tenant-Domain': window.location.hostname
        }

        if (token) {
            headers['Authorization'] = `Bearer ${token}`
        }

        return headers
    }

    /**
     * Get withdrawal settings for current tenant
     */
    async getWithdrawalSettings(): Promise<WithdrawalSettingsResponse> {
        try {
            const tenantId = await this.resolveRequestTenantId()
            const response = await fetch(
                `${this.baseUrl}/settings?tenantScope=${tenantId}&environment=all`,
                {
                    method: 'GET',
                    headers: this.buildHeaders(tenantId)
                }
            )

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            return data
        } catch (error) {
            console.error('Error fetching withdrawal settings:', error)
            throw error
        }
    }

    /**
     * Get specific withdrawal configuration value
     */
    async getWithdrawalConfig(configKey: string): Promise<WithdrawalConfigResponse> {
        try {
            const tenantId = await this.resolveRequestTenantId()
            const response = await fetch(
                `${this.baseUrl}/config/${configKey}?tenantScope=${tenantId}&environment=all`,
                {
                    method: 'GET',
                    headers: this.buildHeaders(tenantId)
                }
            )

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            return data
        } catch (error) {
            console.error(`Error fetching withdrawal config ${configKey}:`, error)
            throw error
        }
    }

    /**
     * Update withdrawal settings for current tenant
     */
    async updateWithdrawalSettings(
        settings: WithdrawalSettings,
        reason: string = 'Updated withdrawal settings'
    ): Promise<WithdrawalSettingsResponse> {
        try {
            const tenantId = await this.resolveRequestTenantId()
            const response = await fetch(
                `${this.baseUrl}/settings`,
                {
                    method: 'PUT',
                    headers: this.buildHeaders(tenantId),
                    body: JSON.stringify({
                        tenantScope: tenantId,
                        settings,
                        reason
                    })
                }
            )

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            return data
        } catch (error) {
            console.error('Error updating withdrawal settings:', error)
            throw error
        }
    }

    /**
     * Get minimum withdrawal amount
     */
    async getMinWithdrawalAmount(): Promise<number> {
        try {
            const response = await this.getWithdrawalConfig('min_withdrawal_amount')
            return response.data.value
        } catch (error) {
            console.error('Error getting min withdrawal amount:', error)
            // Return default value if API fails
            return 500
        }
    }

    /**
     * Get withdrawal fee percentage
     */
    async getWithdrawalFee(): Promise<number> {
        try {
            const response = await this.getWithdrawalConfig('withdrawal_fee')
            return response.data.value
        } catch (error) {
            console.error('Error getting withdrawal fee:', error)
            return 0
        }
    }

    /**
     * Get maximum auto approval amount
     */
    async getMaxAutoApprovalAmount(): Promise<number> {
        try {
            const response = await this.getWithdrawalConfig('max_auto_approval_amount')
            return response.data.value
        } catch (error) {
            console.error('Error getting max auto approval amount:', error)
            return 4999
        }
    }

    /**
     * Get daily withdrawal limit
     */
    async getDailyWithdrawalLimit(): Promise<number> {
        try {
            const response = await this.getWithdrawalConfig('daily_withdrawal_limit')
            return response.data.value
        } catch (error) {
            console.error('Error getting daily withdrawal limit:', error)
            return 0
        }
    }

    /**
     * Get games before withdrawal requirement
     */
    async getGamesBeforeWithdrawal(): Promise<number> {
        try {
            const response = await this.getWithdrawalConfig('games_before_withdrawal')
            return response.data.value
        } catch (error) {
            console.error('Error getting games before withdrawal:', error)
            return 0
        }
    }

    /**
     * Get withdrawal bot shutdown time
     */
    async getWithdrawalBotShutdownTime(): Promise<string> {
        try {
            const response = await this.getWithdrawalConfig('withdrawal_bot_shutdown_time')
            return response.data.value
        } catch (error) {
            console.error('Error getting withdrawal bot shutdown time:', error)
            return '23:40'
        }
    }

    /**
     * Get withdrawal bot activation time
     */
    async getWithdrawalBotActivationTime(): Promise<string> {
        try {
            const response = await this.getWithdrawalConfig('withdrawal_bot_activation_time')
            return response.data.value
        } catch (error) {
            console.error('Error getting withdrawal bot activation time:', error)
            return '08:00'
        }
    }

    /**
     * Check if withdrawal is currently disabled based on bot schedule
     */
    async isWithdrawalDisabled(): Promise<boolean> {
        try {
            const shutdownTime = await this.getWithdrawalBotShutdownTime()
            const activationTime = await this.getWithdrawalBotActivationTime()

            const now = new Date()
            const currentHour = now.getHours()
            const currentMinute = now.getMinutes()
            const currentTime = currentHour * 60 + currentMinute

            // Parse shutdown time (e.g., "23:40")
            const [shutdownHour, shutdownMinute] = shutdownTime.split(':').map(Number)
            const shutdownTimeMinutes = shutdownHour * 60 + shutdownMinute

            // Parse activation time (e.g., "08:00")
            const [activationHour, activationMinute] = activationTime.split(':').map(Number)
            const activationTimeMinutes = activationHour * 60 + activationMinute

            // Check if current time is within the disabled period
            if (shutdownTimeMinutes > activationTimeMinutes) {
                // Normal case: shutdown time is later than activation time (e.g., 23:40 - 08:00)
                return currentTime >= shutdownTimeMinutes || currentTime <= activationTimeMinutes
            } else {
                // Edge case: shutdown time is earlier than activation time (e.g., 08:00 - 23:40)
                return currentTime >= shutdownTimeMinutes && currentTime <= activationTimeMinutes
            }
        } catch (error) {
            console.error('Error checking withdrawal status:', error)
            // Return false (enabled) if there's an error
            return false
        }
    }

    /**
     * Get withdrawal disabled message
     */
    async getWithdrawalDisabledMessage(): Promise<string> {
        try {
            const shutdownTime = await this.getWithdrawalBotShutdownTime()
            const activationTime = await this.getWithdrawalBotActivationTime()
            return `ปิดทำรายการถอนชั่วคราว เวลา ${shutdownTime} - ${activationTime} น. ขออภัยในความไม่สะดวกค่ะ / ธนาคารมีปัญหา / ปรับปรุงระบบ`
        } catch (error) {
            console.error('Error getting withdrawal disabled message:', error)
            return 'ปิดทำรายการถอนชั่วคราว ขออภัยในความไม่สะดวกค่ะ'
        }
    }
}

// Export singleton instance
export const withdrawalSettingsService = new WithdrawalSettingsService()
export default withdrawalSettingsService
